.reader {
  max-width: var(--content-max-width);
  margin: 0 auto;
  padding: var(--space-xl);
}

.back {
  margin-bottom: var(--space-lg);
}

.header {
  margin-bottom: var(--space-2xl);
  padding-bottom: var(--space-xl);
  border-bottom: 1px solid var(--border);
}

.titleRow {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-md);
}

.metaLeft {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.date {
  font-family: var(--font-code);
  font-size: var(--text-sm);
  color: var(--text-muted);
}

.category {
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  color: var(--accent-magenta);
  letter-spacing: 2px;
}

.title {
  font-family: var(--font-heading);
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--space-md);
  text-shadow: 0 0 30px var(--glow-cyan);
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-xs);
}

.content {
  font-size: var(--text-base);
  line-height: 1.8;
  color: var(--text-primary);
  /* 长词/URL 安全换行，不撑破版心 */
  overflow-wrap: break-word;
}

/* ── 标题层级：字号递减 + 上松下紧节奏（Apple 8pt 栅格） ── */
.content h1,
.content h2,
.content h3,
.content h4,
.content h5,
.content h6 {
  line-height: 1.3;
}

/* 正文内 h1 此前无字号约束，会回落到浏览器默认 2em，破坏层级节奏 */
.content h1 {
  font-family: var(--font-heading);
  font-size: var(--text-2xl);
  margin-top: var(--space-xl);
  margin-bottom: var(--space-md);
  color: var(--accent-cyan);
}

.content h1:first-child,
.content h2:first-child,
.content h3:first-child {
  margin-top: 0;
}

.content h2,
.content h3 {
  font-family: var(--font-heading);
  margin-top: var(--space-2xl);
  margin-bottom: var(--space-md);
  color: var(--accent-cyan);
}

.content h2 {
  font-size: var(--text-2xl);
  border-bottom: 1px solid var(--border);
  padding-bottom: var(--space-sm);
}

.content h3 {
  font-size: var(--text-xl);
}

.content h4 {
  font-size: var(--text-lg);
  font-family: var(--font-heading);
  margin-top: var(--space-xl);
  margin-bottom: var(--space-sm);
  color: var(--accent-magenta);
  padding-left: var(--space-md);
  border-left: 3px solid var(--accent-magenta);
}

.content h5 {
  font-size: var(--text-md);
  font-family: var(--font-heading);
  margin-top: var(--space-lg);
  margin-bottom: var(--space-sm);
  color: var(--text-secondary);
}

.content h6 {
  font-size: var(--text-sm);
  font-family: var(--font-heading);
  margin-top: var(--space-lg);
  margin-bottom: var(--space-sm);
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.content p {
  margin-bottom: var(--space-md);
}

.content ul,
.content ol {
  margin-bottom: var(--space-md);
  padding-left: var(--space-xl);
}

/* 列表项 8px 间距（Apple List 行距标准），嵌套列表收紧 */
.content li {
  margin-bottom: var(--space-sm);
}

.content li:last-child {
  margin-bottom: 0;
}

.content li > ul,
.content li > ol {
  margin-top: var(--space-sm);
  margin-bottom: 0;
}

.content strong {
  color: var(--text-primary);
  font-weight: 700;
}

/* ── 行内 code：pill 徽章式（区别于块级 code-block 卡片） ── */
.content code:not(pre code) {
  font-family: var(--font-code);
  font-size: 0.875em;
  padding: 2px 6px;
  background: rgba(0, 240, 255, 0.08);
  border: 1px solid rgba(0, 240, 255, 0.15);
  border-radius: 6px;
  color: var(--accent-cyan);
}

.content blockquote {
  border-left: 3px solid var(--accent-cyan);
  margin: var(--space-lg) 0;
  padding: var(--space-md) var(--space-lg);
  color: var(--text-secondary);
  font-style: italic;
  background: var(--bg-surface);
  border-radius: 0 var(--radius-md) var(--radius-md) 0;
}

.content blockquote p:last-child {
  margin-bottom: 0;
}

/* ── 图片：约束宽度 + 圆角 + 居中 ── */
.content img {
  max-width: 100%;
  height: auto;
  border-radius: var(--radius-md);
  border: 1px solid var(--border);
  display: block;
  margin: var(--space-lg) auto;
}

/* ── 表格：卡片容器 + 表头分隔 + 8pt 单元格内边距 ── */
.content table {
  display: block;
  width: max-content;
  max-width: 100%;
  overflow-x: auto;
  border-collapse: collapse;
  margin: var(--space-lg) 0;
  font-size: var(--text-sm);
}

.content thead {
  border-bottom: 1px solid var(--border);
}

.content th {
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-cyan);
  text-align: left;
  padding: var(--space-sm) var(--space-md);
  white-space: nowrap;
}

.content td {
  padding: var(--space-sm) var(--space-md);
  color: var(--text-secondary);
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
}

.content tbody tr:last-child td {
  /* 末行保留边框，与表头分隔线呼应，闭合表格轮廓 */
  border-bottom: 1px solid var(--border);
}

.content tbody tr:hover {
  background: rgba(0, 240, 255, 0.04);
}

/* 数字右对齐规则移除（markdown 无法标记数值列，无实际效果） */

.content pre {
  margin: 0;
  padding: var(--space-md);
  overflow-x: auto;
  background: var(--bg-primary);
  border: none;
}

/* ── 代码块独立小框（卡片式） ── */
.content :global(.code-block) {
  position: relative;
  margin: var(--space-lg) 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--bg-primary);
  box-shadow: var(--shadow-md);
  overflow: hidden;
}

/* 左侧霓虹渐变描边，强化“独立框”识别度 */
.content :global(.code-block)::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--accent-cyan), var(--accent-magenta));
  opacity: 0.75;
}

.content :global(.code-block__header) {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-xs) var(--space-md);
  background: var(--bg-elevated);
  border-bottom: 1px solid var(--border);
}

.content :global(.code-block__lang) {
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  letter-spacing: 2px;
  color: var(--accent-cyan);
  text-shadow: 0 0 8px var(--glow-cyan);
}

.content :global(.code-block__copy) {
  font-family: var(--font-heading);
  font-size: var(--text-xs);
  letter-spacing: 1px;
  color: var(--text-secondary);
  padding: 2px 8px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: transparent;
  transition: all var(--transition-fast);
}

.content :global(.code-block__copy:hover) {
  color: var(--accent-cyan);
  border-color: var(--accent-cyan);
  box-shadow: 0 0 8px var(--glow-cyan);
}

.content :global(.code-block__copy.is-copied) {
  color: var(--accent-green);
  border-color: var(--accent-green);
  box-shadow: 0 0 8px var(--glow-green);
}

/* ── 代码语法高亮（hljs token 配色，对齐 cyberpunk 调色板） ── */
.content :global(.hljs) {
  color: var(--text-primary);
}

.content :global(.hljs-comment),
.content :global(.hljs-quote) {
  color: var(--text-muted);
  font-style: italic;
}

.content :global(.hljs-keyword),
.content :global(.hljs-selector-tag),
.content :global(.hljs-literal),
.content :global(.hljs-doctag),
.content :global(.hljs-name),
.content :global(.hljs-tag),
.content :global(.hljs-meta) {
  color: var(--accent-magenta);
}

.content :global(.hljs-string),
.content :global(.hljs-regexp),
.content :global(.hljs-symbol),
.content :global(.hljs-addition),
.content :global(.hljs-template-string) {
  color: var(--accent-green);
}

.content :global(.hljs-number),
.content :global(.hljs-built_in),
.content :global(.hljs-params),
.content :global(.hljs-type) {
  color: var(--accent-yellow);
}

.content :global(.hljs-title),
.content :global(.hljs-section),
.content :global(.hljs-selector-id),
.content :global(.hljs-selector-class),
.content :global(.hljs-attr),
.content :global(.hljs-attribute),
.content :global(.hljs-variable) {
  color: var(--accent-cyan);
}

.content :global(.hljs-title.function_),
.content :global(.hljs-title.class_) {
  color: var(--accent-magenta);
}

.content :global(.hljs-operator),
.content :global(.hljs-punctuation),
.content :global(.hljs-property),
.content :global(.hljs-template-variable) {
  color: var(--text-primary);
}

.content :global(.hljs-deletion) {
  color: #ff6b6b;
  background: rgba(255, 45, 123, 0.12);
}

.content :global(.hljs-addition) {
  background: rgba(57, 255, 20, 0.12);
}

.content code {
  font-family: var(--font-code);
}

/* 块级代码字号用绝对值；行内 code 的字号在上方 pill 规则中用 em 相对正文 */
.content pre code {
  font-size: var(--text-sm);
}

.content a {
  color: var(--accent-cyan);
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color var(--transition-fast);
}

.content a:hover {
  border-bottom-color: var(--accent-cyan);
}

/* ── 分割线：日记式节点装饰 ──
   居中一颗呼吸发光的菱形节点，两侧渐隐线；
   ::before/::after 承载左右线段，hr 本体为节点容器 */
.content hr {
  border: none;
  margin: var(--space-2xl) auto;
  width: 100%;
  height: 1px;
  position: relative;
  overflow: visible;
  background: linear-gradient(90deg, transparent 0%, var(--border) 20%, var(--border) 80%, transparent 100%);
}

/* 中央菱形节点：呼吸发光 + 缓慢旋转 */
.content hr::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 10px;
  height: 10px;
  transform: translate(-50%, -50%) rotate(45deg);
  background: var(--accent-cyan);
  box-shadow: 0 0 12px var(--glow-cyan);
  animation: hr-node-breathe 3s ease-in-out infinite;
}

@keyframes hr-node-breathe {
  0%, 100% {
    box-shadow: 0 0 8px var(--glow-cyan);
    opacity: 0.8;
  }
  50% {
    box-shadow: 0 0 22px var(--glow-cyan);
    opacity: 1;
  }
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-3xl);
  color: var(--accent-cyan);
  font-family: var(--font-heading);
  font-size: var(--text-sm);
  letter-spacing: 2px;
  gap: var(--space-md);
}

.loadingBar {
  width: 200px;
  height: 2px;
  background: var(--border);
  border-radius: 1px;
  overflow: hidden;
}

.loadingBar::after {
  content: '';
  display: block;
  width: 40%;
  height: 100%;
  background: var(--accent-cyan);
  animation: loading 1s ease-in-out infinite;
}

@keyframes loading {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(350%); }
}

.notFound {
  text-align: center;
  padding: var(--space-3xl);
}

.notFound h2 {
  font-family: var(--font-heading);
  color: var(--accent-magenta);
  margin-bottom: var(--space-md);
}

.notFound p {
  color: var(--text-muted);
  margin-bottom: var(--space-xl);
}


